import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, Facebook, MessageCircle, Package, Truck, Sparkles,
  Heart, Zap, Gift, ArrowRight, Clock, ShieldCheck,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// External URLs — single source of truth.
const ESHOP_URL = 'https://olion.odoo.com/shop';
const FB_OLION_BOX_URL = 'https://www.facebook.com/profile.php?id=61591172592428';
const WHATSAPP_NUMBER = '21695806095';

const WHATSAPP_PREFILLS = {
  fr: "Bonjour Olion Box, j'aimerais commander une box personnalisée / discuter d'un combo.",
  en: "Hello Olion Box, I'd like to order a personalised box / discuss a combo.",
  ar: 'مرحبا أوليون بوكس، أود طلب صندوق مخصص / مناقشة عرض كومبو.',
};

// Mascot delivery photo uploaded by the user (Olion lion on scooter).
const MASCOT_PHOTO =
  'https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/k49j06kl_WhatsApp%20Image%202026-02-08%20at%2008.28.21.jpeg';

const CONTENT = {
  fr: {
    badge: 'Olion Box · Livraison à domicile',
    title: "Vos détergents préférés livrés chez vous, en un clic.",
    subtitle:
      "Composez votre Olion Box personnalisée ou choisissez n'importe quel produit de notre e-shop — livraison rapide partout en Tunisie, prix imbattables, qualité garantie.",
    primaryCta: "Commander sur l'E-shop",
    fbCta: 'Voir les Box & Combos',
    waCta: 'Discuter avec un conseiller',
    waSub: 'Réponse rapide sur WhatsApp',
    sectionHowKicker: 'Comment ça marche',
    sectionHowTitle: '3 étapes pour recevoir votre Olion Box',
    steps: [
      { icon: ShoppingBag, title: 'Choisissez', desc: 'Composez votre box ou sélectionnez vos produits préférés depuis notre e-shop.' },
      { icon: Package, title: 'Préparons', desc: 'Notre équipe prépare votre commande avec soin et l’emballe pour la livraison.' },
      { icon: Truck, title: 'Livraison', desc: 'Notre livreur Olion vient directement chez vous, partout en Tunisie.' },
    ],
    benefitsKicker: 'Pourquoi Olion Box',
    benefitsTitle: 'Tout ce qu’il vous faut, en une seule commande',
    benefits: [
      { icon: Heart, title: 'Box personnalisées', desc: 'Choisissez parfums, formats et quantités selon vos besoins.' },
      { icon: Zap, title: 'Livraison rapide', desc: 'Expédition sous 24-48 heures dans la majorité des régions.' },
      { icon: Gift, title: 'Combos exclusifs', desc: 'Offres et lots disponibles uniquement via la Box.' },
      { icon: ShieldCheck, title: 'Qualité garantie', desc: 'Produits authentiques directement de notre usine à Sfax.' },
      { icon: Clock, title: 'Disponible 7j/7', desc: 'Commandez à tout moment depuis notre e-shop.' },
      { icon: Sparkles, title: 'Tarifs imbattables', desc: 'Prix usine, sans intermédiaire, sans surprise.' },
    ],
    ctaTitle: 'Prêt à commander votre première Olion Box ?',
    ctaDesc: 'Démarrez sur notre e-shop ou contactez-nous directement sur WhatsApp.',
  },
  en: {
    badge: 'Olion Box · Home delivery',
    title: 'Your favourite detergents, delivered to your door in one click.',
    subtitle:
      "Build your personalised Olion Box or pick any product from our e-shop — fast delivery across Tunisia, unbeatable prices, guaranteed quality.",
    primaryCta: 'Order on the E-shop',
    fbCta: 'See Boxes & Combos',
    waCta: 'Chat with a sales advisor',
    waSub: 'Quick reply on WhatsApp',
    sectionHowKicker: 'How it works',
    sectionHowTitle: '3 steps to receive your Olion Box',
    steps: [
      { icon: ShoppingBag, title: 'Choose', desc: 'Build your box or pick your favourite products from our e-shop.' },
      { icon: Package, title: 'We prepare', desc: 'Our team carefully prepares and packs your order for delivery.' },
      { icon: Truck, title: 'Delivery', desc: 'Our Olion driver brings it straight to your door, anywhere in Tunisia.' },
    ],
    benefitsKicker: 'Why Olion Box',
    benefitsTitle: 'Everything you need, in a single order',
    benefits: [
      { icon: Heart, title: 'Personalised boxes', desc: 'Choose fragrances, sizes and quantities to fit your needs.' },
      { icon: Zap, title: 'Fast delivery', desc: 'Shipping within 24-48 hours across most regions.' },
      { icon: Gift, title: 'Exclusive combos', desc: 'Deals and bundles available only through the Box.' },
      { icon: ShieldCheck, title: 'Guaranteed quality', desc: 'Authentic products straight from our Sfax factory.' },
      { icon: Clock, title: 'Available 24/7', desc: 'Order anytime from our e-shop.' },
      { icon: Sparkles, title: 'Unbeatable pricing', desc: 'Factory prices, no middleman, no surprises.' },
    ],
    ctaTitle: 'Ready to order your first Olion Box?',
    ctaDesc: 'Get started on our e-shop or reach us directly on WhatsApp.',
  },
  ar: {
    badge: 'أوليون بوكس · توصيل للمنزل',
    title: 'منظفاتك المفضلة تصل إلى باب بيتك بنقرة واحدة.',
    subtitle:
      'صمم صندوق أوليون المخصص لك أو اختر أي منتج من متجرنا الإلكتروني — توصيل سريع في كل تونس، أسعار لا تُهزم، جودة مضمونة.',
    primaryCta: 'اطلب من المتجر الإلكتروني',
    fbCta: 'شاهد الصناديق والعروض',
    waCta: 'تحدث مع مستشار المبيعات',
    waSub: 'رد سريع على واتساب',
    sectionHowKicker: 'كيف يعمل',
    sectionHowTitle: '3 خطوات لتصلك أوليون بوكس',
    steps: [
      { icon: ShoppingBag, title: 'اختر', desc: 'صمم صندوقك أو اختر منتجاتك المفضلة من متجرنا.' },
      { icon: Package, title: 'نحضّر طلبك', desc: 'يقوم فريقنا بتحضير طلبك بعناية وتغليفه للتوصيل.' },
      { icon: Truck, title: 'التوصيل', desc: 'يأتي مندوب أوليون مباشرة إلى منزلك في جميع أنحاء تونس.' },
    ],
    benefitsKicker: 'لماذا أوليون بوكس',
    benefitsTitle: 'كل ما تحتاجه في طلب واحد',
    benefits: [
      { icon: Heart, title: 'صناديق مخصصة', desc: 'اختر العطور والأحجام والكميات حسب احتياجاتك.' },
      { icon: Zap, title: 'توصيل سريع', desc: 'شحن خلال 24-48 ساعة في معظم المناطق.' },
      { icon: Gift, title: 'عروض حصرية', desc: 'صفقات وحزم متاحة فقط عبر البوكس.' },
      { icon: ShieldCheck, title: 'جودة مضمونة', desc: 'منتجات أصلية مباشرة من مصنعنا في صفاقس.' },
      { icon: Clock, title: 'متاح 7/7', desc: 'اطلب في أي وقت من متجرنا الإلكتروني.' },
      { icon: Sparkles, title: 'أسعار لا تُهزم', desc: 'أسعار المصنع، بلا وسطاء، بلا مفاجآت.' },
    ],
    ctaTitle: 'هل أنت مستعد لطلب أول أوليون بوكس؟',
    ctaDesc: 'ابدأ على متجرنا الإلكتروني أو تواصل مباشرة على واتساب.',
  },
};

const CtaCard = ({ icon: Icon, label, sub, href, gradient, testid, direction }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-5 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-white block ${
      direction === 'rtl' ? 'text-right' : ''
    }`}
  >
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 group-hover:scale-150 transition-transform duration-700" />
    <div className="relative flex items-center gap-4">
      <div className="shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-base leading-snug">{label}</div>
        {sub && <div className="text-xs text-white/85 mt-0.5">{sub}</div>}
      </div>
      <ArrowRight
        className={`w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform ${direction === 'rtl' ? 'rotate-180 group-hover:-translate-x-1' : ''}`}
      />
    </div>
  </a>
);

export const OlionBoxSection = () => {
  const { language, direction } = useLanguage();
  const c = CONTENT[language] || CONTENT.fr;
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_PREFILLS[language] || WHATSAPP_PREFILLS.fr,
  )}`;

  return (
    <section id="olion-box" data-testid="olion-box-section" dir={direction}>
      {/* HERO — mascot photo as full background, content overlay on the left */}
      <div className="relative overflow-hidden bg-blue-900 text-white min-h-[640px] sm:min-h-[720px]">
        {/* Background mascot photo */}
        <img
          src={MASCOT_PHOTO}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: direction === 'rtl' ? 'left center' : 'right center' }}
        />
        {/* Color overlay — solid blue on text side, fades over the mascot */}
        <div
          className={`absolute inset-0 ${
            direction === 'rtl'
              ? 'bg-gradient-to-l from-blue-950/95 via-blue-900/85 to-blue-800/10'
              : 'bg-gradient-to-r from-blue-950/95 via-blue-900/85 to-blue-800/10'
          }`}
        />
        {/* Warm orange wash on the opposite side, soft-light blending for warmth */}
        <div
          className={`absolute inset-0 ${
            direction === 'rtl'
              ? 'bg-gradient-to-tl from-transparent via-transparent to-orange-500/40'
              : 'bg-gradient-to-tr from-transparent via-transparent to-orange-500/40'
          } mix-blend-soft-light pointer-events-none`}
        />
        {/* Subtle radial highlights */}
        <div className="absolute inset-0 opacity-20 pointer-events-none [background-image:radial-gradient(circle_at_20%_30%,white,transparent_40%),radial-gradient(circle_at_85%_70%,white,transparent_45%)]" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
          <div className={`max-w-xl ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="inline-flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 shadow-lg">
              <Sparkles className="w-3.5 h-3.5" />
              {c.badge}
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5 drop-shadow-lg">
              {c.title}
            </h2>
            <p className="text-base sm:text-lg text-white/95 leading-relaxed max-w-lg drop-shadow">
              {c.subtitle}
            </p>

            {/* CTA Stack */}
            <div className="mt-8 grid sm:grid-cols-1 gap-3 max-w-md">
              <CtaCard
                icon={ShoppingBag}
                label={c.primaryCta}
                href={ESHOP_URL}
                gradient="from-orange-500 to-red-500"
                testid="olion-box-eshop-cta"
                direction={direction}
              />
              <CtaCard
                icon={Facebook}
                label={c.fbCta}
                href={FB_OLION_BOX_URL}
                gradient="from-blue-500 to-blue-700"
                testid="olion-box-fb-cta"
                direction={direction}
              />
              <CtaCard
                icon={MessageCircle}
                label={c.waCta}
                sub={c.waSub}
                href={waHref}
                gradient="from-green-500 to-emerald-600"
                testid="olion-box-whatsapp-cta"
                direction={direction}
              />
            </div>
          </div>

          {/* Floating "Livraison rapide 24-48h" badge — bottom corner over the mascot */}
          <div
            className={`hidden md:flex absolute bottom-8 ${
              direction === 'rtl' ? 'left-8' : 'right-8'
            } bg-white text-blue-900 rounded-2xl shadow-2xl px-4 py-3 items-center gap-3 animate-bounce`}
          >
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div className={direction === 'rtl' ? 'text-right' : 'text-left'}>
              <div className="text-xs font-bold leading-tight">
                {language === 'ar' ? 'توصيل سريع' : language === 'en' ? 'Fast delivery' : 'Livraison rapide'}
              </div>
              <div className="text-[10px] text-gray-500 leading-tight">24-48h</div>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS — 3 steps */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`max-w-2xl mb-10 ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">
              {c.sectionHowKicker}
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 leading-tight">
              {c.sectionHowTitle}
            </h3>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden sm:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-200" />
            {c.steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`olion-box-step-${i}`}
                  className={`relative bg-white border border-gray-100 rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                >
                  <div className="relative w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center shadow-lg ring-2 ring-white">
                      {i + 1}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 text-center mb-2">{s.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed text-center">{s.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`max-w-2xl mb-10 ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              {c.benefitsKicker}
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 leading-tight">
              {c.benefitsTitle}
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.benefits.map((b, i) => {
              const Icon = b.icon;
              const accents = [
                'from-pink-500 to-rose-600',
                'from-amber-500 to-orange-600',
                'from-purple-500 to-fuchsia-600',
                'from-emerald-500 to-teal-600',
                'from-blue-500 to-indigo-600',
                'from-orange-500 to-red-600',
              ];
              return (
                <article
                  key={i}
                  data-testid={`olion-box-benefit-${i}`}
                  className={`group flex gap-4 bg-white p-5 rounded-2xl border border-gray-100 hover:border-transparent hover:shadow-xl transition-all ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}
                >
                  <div
                    className={`shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${accents[i % accents.length]} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{b.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      {/* FINAL CTA STRIP */}
      <div className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-8 sm:p-12 text-white shadow-2xl ${
              direction === 'rtl' ? 'text-right' : ''
            }`}
          >
            <div className="absolute inset-0 opacity-25 pointer-events-none [background-image:radial-gradient(circle_at_20%_30%,white,transparent_40%),radial-gradient(circle_at_85%_70%,white,transparent_45%)]" />
            <div className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-6 ${direction === 'rtl' ? 'lg:flex-row-reverse' : ''}`}>
              <div className="flex-1">
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 leading-tight">{c.ctaTitle}</h3>
                <p className="text-white/95 text-sm sm:text-base">{c.ctaDesc}</p>
              </div>
              <div className={`flex flex-wrap gap-3 ${direction === 'rtl' ? 'justify-end' : ''}`}>
                <a
                  href={ESHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="olion-box-final-eshop"
                  className="inline-flex items-center gap-2 bg-white text-orange-600 hover:bg-gray-100 px-6 py-3.5 rounded-full font-bold text-sm shadow-xl transition-all hover:scale-105 whitespace-nowrap"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {c.primaryCta}
                </a>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="olion-box-final-whatsapp"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3.5 rounded-full font-bold text-sm shadow-xl transition-all hover:scale-105 whitespace-nowrap"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Standalone page wrapper around the section.
export const OlionBoxPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  return <OlionBoxSection />;
};
